// 必须在所有其他导入之前导入，确保 polyfill 在其他库使用 Array.at 之前就可用
import './utils/array-at-polyfill.js'
import './request-from'
import { createApp } from 'vue'
import { createPinia, setActivePinia } from 'pinia'
import { bootstrap } from '@h5-journey/bootstrap'
import { setGlobalPinia } from '@h5-journey/stores'
import { router } from '@/router'
import { bridge } from '@/common/bridge/methods'
import { BRIDGE_METHODS } from '@/common/bridge/constants'
import {
  getLegacyUserSession,
  setLegacyUserInfo,
  syncLegacyUserSession,
  useUserStoreOutside,
} from '@/pinia/user'
import { handleBridgeResponse } from '@/common/functions/handleBridgeResponse'
import i18n from './i18n'
import 'vant/lib/index.css'
import './index.css'
import './assets/styles/tailwind.css'
import './theme/index.scss' // 预先加载全局初始化css
import './assets/font/iconfont.css'
import './assets/styles/fonts.scss'
import App from './App.vue'
import VConsolePlugin from './utils/vconsole'
import qs from 'qs'
// 导入 canvas polyfill，解决 pdfh5 兼容性问题
import './utils/canvas-polyfill.js'
// 导入 Array.at polyfill，解决 marked.js 兼容性问题
import './utils/array-polyfill.js'
import {
  isInHostMiniAppWebView,
  markHostMiniAppSession,
  shouldClearStaleHostMiniAppSession,
  clearStaleHostMiniAppSession,
} from '@/utils/tool.js'
import { captureChannelSource } from '@/utils/channelSource'
import { getCouponWindowDetail } from '@/service/api/coupon'
import { getThirdUserInfo } from '@/service/api/pay'
// 导入埋点指令
import trackDirective from './directives/track'
import { VueMasonryPlugin } from 'vue-masonry'
import axios from 'axios'
import { init, captureException } from '@umengfe/apm'
import { syncLoginToken } from '@/hongkongimprint/composables/useAppState'

// 创建应用实例
const app = createApp(App)

app.directive('longpress', {
  mounted(el, binding) {
    let pressTimer = null
    let startX = 0
    let startY = 0
    const moveThreshold = 10 // 滑动超过 10px 视为滚动，不触发长按

    const start = e => {
      el.style.webkitTouchCallout = 'none'
      el.style.webkitUserSelect = 'none'
      el.style.khtmlUserSelect = 'none'
      el.style.mozUserSelect = 'none'
      el.style.msUserSelect = 'none'
      el.style.userSelect = 'none'

      const point = e.touches ? e.touches[0] : e
      startX = point.clientX
      startY = point.clientY

      if (pressTimer === null) {
        pressTimer = setTimeout(() => {
          // 长按1000毫秒后执行回调，避免误触举报
          handler(e)
        }, 1000)
      }
    }

    const cancel = () => {
      if (pressTimer !== null) {
        clearTimeout(pressTimer)
        pressTimer = null
      }
    }

    const onMove = e => {
      if (!pressTimer) return
      const point = e.touches ? e.touches[0] : e
      const deltaX = Math.abs(point.clientX - startX)
      const deltaY = Math.abs(point.clientY - startY)
      if (deltaX > moveThreshold || deltaY > moveThreshold) {
        // 认为是滑动，取消长按
        cancel()
      }
    }

    const handler = e => {
      if (binding.value instanceof Function) {
        binding.value(e)
      }
    }

    el.addEventListener('mousedown', start, false)
    el.addEventListener('touchstart', start, false)
    el.addEventListener('mousemove', onMove, false)
    el.addEventListener('touchmove', onMove, false)
    el.addEventListener('mouseup', cancel, false)
    el.addEventListener('mouseout', cancel, false)
    el.addEventListener('touchend', cancel, false)
    el.addEventListener('touchcancel', cancel, false)
  },
  unmounted(el) {
    // 这里暂时不移除事件监听，保持与原逻辑一致
  },
})

// 安装 pinia、router 和 i18n（显式实例并注册全局，供 bootstrap 在组件外使用共享 store）
const pinia = createPinia()
setActivePinia(pinia)
setGlobalPinia(pinia)
app.use(pinia).use(router).use(i18n)

app.use(VConsolePlugin)

app.use(VueMasonryPlugin)
// 注册埋点指令
app.directive('track', trackDirective)
// 开发环境注入调试用户信息（通过 .env.development 配置，不影响生产）
console.log('import.meta.env.MODE',import.meta.env.MODE);

if (import.meta.env.MODE === 'development') {
  const debugUserInfo = {
    mobile: import.meta.env.VITE_DEBUG_MOBILE,
    loginToken: import.meta.env.VITE_DEBUG_LOGIN_TOKEN,
    loginId: import.meta.env.VITE_DEBUG_LOGIN_ID,
    memberId: import.meta.env.VITE_DEBUG_MEMBER_ID,
  }
  Object.entries(debugUserInfo).forEach(([key, value]) => {
    if (value) sessionStorage.setItem(key, value)
  })
}

/**
 * 宿主 WebView 写入用户会话 key（微信 / 元服务同一套）
 * 写入 sessionStorage 后删除 localStorage，避免元服务退出后旧 token 被救活。
 */
const persistHostSessionKey = (key, urlValue) => {
  if (urlValue) {
    sessionStorage.removeItem(key)
    sessionStorage.setItem(key, urlValue)
    localStorage.removeItem(key)
  } else {
    const existing = sessionStorage.getItem(key) || localStorage.getItem(key)
    if (existing) {
      sessionStorage.removeItem(key)
      sessionStorage.setItem(key, existing)
      localStorage.removeItem(key)
    }
  }
}

// 调试的时候塞入memberId和terminalCode
// sessionStorage.setItem('memberId', '1c1c7e09966a4b71a26cfd1a91e91685')
// terminalCode/channelId/sourceFlag 默认值由 bootstrap 接管（URL channel 参数 > 已有值 > 默认兜底）
// sessionStorage.setItem('loginToken', '6a3a8b4dcb754d7db858792d21eca3ca1779946268604')

// userStore.setUserInfo({
//   mobile: '15072313771',
//   memberId: '13ee3545f2494ad6b70c330deaf2a39e',
//   loginId: 'U2001632542',
//   loginToken: '5482ab984cf34a9f85f56b2093315d6c1766125093130',
// })

/**
 * 是否为跳第三方的中转页（/redirect）。
 * history 模式看 pathname，hash 模式看 hash；与路由模式无关，入口阶段直接判 URL 最可靠。
 */
const isRedirectPage = () => {
  const routePath = (import.meta.env.VITE_ROUTE_PATH || '/').replace(/\/+$/, '')
  return (
    window.location.pathname.replace(/\/+$/, '') === `${routePath}/redirect` ||
    window.location.hash.startsWith('#/redirect')
  )
}

// 微信小程序环境参数获取函数
const getWeChatMiniProgramParams = async () => {
  // 独立 H5 新入口时清除宿主 WebView 残留，避免误判小程序样式环境
  if (shouldClearStaleHostMiniAppSession()) {
    clearStaleHostMiniAppSession()
  }

  // 采集埋点渠道参数（entryChannel/entryScene/wxScene，仅 client=wxapp 生效，与业务 sourceFlag 解耦）
  captureChannelSource()

  const search = window.location.search.replace('?', '')
  const urlParams = qs.parse(search) || {}
  const {
    mobile = '',
    memberId = '',
    loginToken = '',
    loginId = '',
    code = '',
    sourceFlag = '',
    showLoginSuccessModal = '',
    client: miniClient = 'wxapp',
    version: miniVersion = 'v6.0.13',
    cuid: miniCuid = '39ca90fa-eed9-4505-a877-65ed397d8199',
  } = urlParams

  if (urlParams.client === 'wxapp') {
    markHostMiniAppSession()
  }

  if (isInHostMiniAppWebView()) {
    const versionStr = miniVersion
    const requestFrom = `client=${miniClient};domain=common;version=${versionStr};cuid=${miniCuid}`
    axios.defaults.headers.common['Request-From'] = requestFrom
    sessionStorage.setItem('Request-From', requestFrom)

    // sourceFlag 存在则写入（下划线转竖线；兼容重复参数产生的数组）
    const miniSourceFlag = Array.isArray(sourceFlag) ? sourceFlag[0] : sourceFlag
    miniSourceFlag &&
      sessionStorage.setItem('sourceFlag', String(miniSourceFlag).replace(/_/g, '|'))
    sessionStorage.setItem('terminalCode', '04')

    // 将所有URL参数存储到sessionStorage中，供埋点使用
    sessionStorage.setItem('wxappBaseParams', JSON.stringify(urlParams))

    // 存储小程序登录 code（wx.login）
    if (code) {
      sessionStorage.removeItem('code')
      sessionStorage.setItem('code', code)
      localStorage.removeItem('code')
    } else {
      const code = sessionStorage.getItem('code') || localStorage.getItem('code')
      if (code) {
        sessionStorage.removeItem('code')
        sessionStorage.setItem('code', code)
        localStorage.removeItem('code')
      }
    }

    // code 有效期仅 5 分钟，进入应用立即换取 loginInfoId 存 sessionStorage，收银台 commonPay 支付时使用
    // 失败不阻断应用启动，仅记录日志；此时 Request-From 已设置，请求携带小程序渠道标识
    // 中转页（/redirect）除外：code 要原样拼给目标第三方链接（B2C），入口消费会导致 B2C 拿到已失效的 code
    if (code && !isRedirectPage()) {
      getThirdUserInfo({ jsCode: code, isShowToast: false })
        .then(res => {
          // loginInfoId 取值路径以联调报文为准
          const loginInfoId = res?.loginInfoId || ''
          if (loginInfoId) {
            sessionStorage.setItem('loginInfoId', loginInfoId)
          } else {
            console.error('getThirdUserInfo 未返回 loginInfoId:', res)
          }
        })
        .catch(error => {
          console.error('getThirdUserInfo 调用失败:', error)
        })
    }

    const urlUserInfo = {
      mobile,
      memberId,
      loginId,
      loginToken,
    }
    const hasUrlUserInfo = Boolean(loginToken)
    const storeUserInfo = useUserStoreOutside().userInfo
    const fallbackUserInfo = storeUserInfo?.loginToken ? storeUserInfo : getLegacyUserSession()
    setLegacyUserInfo(hasUrlUserInfo ? urlUserInfo : fallbackUserInfo, {
      clearMissing: hasUrlUserInfo,
    })
    if (showLoginSuccessModal === '1') {
      getCouponWindowDetail().then(resJson => {
        const data = resJson?.data ?? resJson ?? {}
        const { newUserFlag = false, userSource = '', picUrl = '' } = data
        sessionStorage.setItem(
          'loginSuccessModal',
          JSON.stringify({ newUserFlag, userSource, picUrl })
        )
      })
    }

    persistHostSessionKey('loginToken', loginToken)
    persistHostSessionKey('memberId', memberId)
    persistHostSessionKey('mobile', mobile)
    persistHostSessionKey('loginId', loginId)
    persistHostSessionKey('code', code)

    return Promise.resolve('宿主 WebView 参数获取完成')
  }
  return Promise.resolve('非宿主 WebView 环境')
}
const hasFlutter = window.flutter_inappwebview
const initializeFlutterUserInfo = async () => {
  if (!hasFlutter) return

  try {
    const res = await bridge.invokeSync(BRIDGE_METHODS.GET_USER_INFO)
    if (res) {
      handleBridgeResponse(res, setLegacyUserInfo, '获取用户信息成功')
    }
  } catch (error) {
    console.error('获取用户信息失败:', error)
  }
}

const initializeFlutterVersion = async () => {
  if (!hasFlutter) return

  try {
    const res = await bridge.invokeSync(BRIDGE_METHODS.GET_VERSION)
    const versionInfo = res?.result?.data || {}
    const client = versionInfo.platform || 'android'
    const rawVersion = versionInfo.version || '6.0.13'
    const version = `v${rawVersion}`
    const cuid = versionInfo.deviceId || '39ca90fa-eed9-4505-a877-65ed397d8199'
    const requestFrom = `client=${client};domain=common;version=${version};cuid=${cuid}`
    sessionStorage.setItem('Request-From', requestFrom)
    axios.defaults.headers.common['Request-From'] = requestFrom
    return res
  } catch (error) {
    console.error('获取版本号失败:', error)
  }
}

const initializeApplication = async () => {
  console.log('=== 开始应用初始化 ===')

  try {
    await bootstrap({
      router,
      trackSource: 'new_journey_h5',
      defaultTerminalCode: '30',
      defaultChannelId: '153',
      defaultSourceFlag: '14|75|76',
    })
    const userStore = useUserStoreOutside()
    if (userStore.loginToken) {
      syncLegacyUserSession(userStore.userInfo || {}, { clearMissing: false })
    } else {
      const legacyUserInfo = getLegacyUserSession()
      if (legacyUserInfo.loginToken) {
        setLegacyUserInfo(legacyUserInfo, { clearMissing: false })
      }
    }
  } catch (error) {
    console.error('bootstrap 初始化失败:', error)
  }

  try {
    await getWeChatMiniProgramParams()
  } catch (error) {
    console.error('获取微信小程序参数失败:', error)
  }

  await Promise.all([router.isReady(), initializeFlutterUserInfo(), initializeFlutterVersion()])

  // 同步 hongkongimprint 模块的登录状态（异步初始化后 sessionStorage 已写入 loginToken）
  syncLoginToken()

  const umengKey = import.meta.env.VITE_UMENG_APPKEY
  if (umengKey) {
    console.log('初始化UMENG');

    init({
      pageFilter: {
        mode: 'ignore',
        rules: [],
      },
      pid: umengKey,
      dsn: '//errlog.umeng.com',
      logLevel: 0,
      tag: sessionStorage.getItem('Request-From') || 'new_journey_h5',
      puid: sessionStorage.getItem('memberId') || '游客',
    })
  }
  app.mount('#app')
}

void initializeApplication()
